import { lazy, Suspense, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useAccount } from '../lib/account'
import StaffLayout from './StaffLayout'
import PageLoader from './PageLoader'

const Login = lazy(() => import('../components/Login'))
const Signup = lazy(() => import('../components/Signup'))
const AcceptInvite = lazy(() => import('../components/AcceptInvite'))
const ClientLogin = lazy(() => import('../components/ClientLogin'))
const CrewPortal = lazy(() => import('../components/crew-portal/CrewPortal'))
const ClientPortalApp = lazy(() => import('../components/ClientPortalApp'))
const Onboarding = lazy(() => import('../components/Onboarding'))

export default function AuthGate() {
  const { session, profile, profileError, loading, isContractor, isOwner, signOut } = useAuth()
  const { account } = useAccount()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  const [showSignup, setShowSignup] = useState(false)

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!session && inviteToken) return <AcceptInvite token={inviteToken} />
  if (!session && showSignup) return <Signup onSwitch={() => setShowSignup(false)} />
  if (!session && window.location.pathname === '/client-login') return <ClientLogin />
  if (!session) return <Login onCreateAccount={() => setShowSignup(true)} />

  if (session && profileError) {
    return (
      <div className="min-h-screen bg-brand-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-[var(--color-danger)] text-4xl mb-4">!</div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Account Setup Incomplete</h2>
          <p className="text-sm text-gray-600 mb-4">
            Your account was created but profile setup didn't complete. Please contact your supervisor or try again.
          </p>
          <button
            onClick={() => signOut()}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm"
          >
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  if (isContractor) return <CrewPortal />
  if (profile?.role === 'client') return <ClientPortalApp />

  // Show onboarding wizard for new owners
  if (isOwner && account && !account.onboarding_completed) {
    return <Suspense fallback={<PageLoader />}><Onboarding /></Suspense>
  }

  return <StaffLayout />
}
